/**
 * B31d — DATE → TIME departure selection (§7 step 1, RULING 2026-07-15).
 * A buyer picks a day from the schedule's horizon, then a departure on
 * that day, with the binding pool's availability on every option. Pure:
 * grouping is a plain function of the slot list (property-tested), the
 * component is markup over it. Day identity uses the DISPLAY time zone —
 * a 23:30 sailing belongs to the buyer's calendar day, not UTC's.
 */
import { useState } from "react";
import { EmptyState, PickerTile } from "@transact/ui";
import type { ReactElement } from "react";
import { formatMoney } from "../lib/money.js";
import { browseAllDoor, emptyDepartureFace } from "./screens.js";
import type { PoolAvailability, SlotSummary } from "./screens.js";

/* ── B521 open leg 2 — per-day prices from the product.calendar read ────
 * The honesty rule rides the wire's shape: TIME carries per-slot COMPUTED
 * prices (a day rule exists), FLAT carries one number the picker draws
 * beside the SELECTED day only — a flat price never fans out into
 * fabricated per-day figures. */

export interface CalendarPriceWire {
  readonly amount: number;
  readonly currency: string;
}
export type CalendarPricingWire =
  | { readonly kind: "FLAT"; readonly from: CalendarPriceWire }
  | { readonly kind: "TIME"; readonly slots: readonly { readonly slotId: string; readonly start: number; readonly from: CalendarPriceWire }[] };

/**
 * Each day's honest figure: the MIN over ITS OWN slots the read priced.
 * FLAT (or a slot the read never priced) contributes nothing — absence,
 * never an invented number.
 */
export function dayFromPrices(
  pricing: CalendarPricingWire,
  groups: readonly DayGroup[],
): Readonly<Record<string, CalendarPriceWire>> {
  if (pricing.kind !== "TIME") return {};
  const bySlot = new Map(pricing.slots.map((s) => [s.slotId, s.from]));
  const out: Record<string, CalendarPriceWire> = {};
  for (const day of groups) {
    for (const d of day.departures) {
      const price = bySlot.get(d.slotId);
      if (price === undefined) continue;
      const held = out[day.key];
      if (held === undefined || price.amount < held.amount) out[day.key] = price;
    }
  }
  return out;
}

/** Whole-unit face for the tile chip ("$30"); cents only when they exist. */
function tilePrice(p: CalendarPriceWire, locale?: string): string {
  const whole = p.amount % 100 === 0;
  const text = formatMoney({ amount: p.amount, currency: p.currency }, locale);
  return whole ? text.replace(/[.,]00$/, "") : text;
}

export interface DayGroup {
  /** Calendar-day identity in the display time zone, YYYY-MM-DD. */
  readonly key: string;
  /** Short weekday, e.g. "Mon". */
  readonly weekday: string;
  /** Day of month as displayed, e.g. "20". */
  readonly dayOfMonth: string;
  /** Full human label for assistive tech, e.g. "Monday, July 20". */
  readonly label: string;
  readonly departures: readonly SlotSummary[];
}

export interface DisplayOptions {
  readonly locale?: string;
  readonly timeZone?: string;
}

function formatters(opts: DisplayOptions): {
  readonly key: Intl.DateTimeFormat;
  readonly weekday: Intl.DateTimeFormat;
  readonly dayOfMonth: Intl.DateTimeFormat;
  readonly label: Intl.DateTimeFormat;
  readonly time: Intl.DateTimeFormat;
} {
  const tz = opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {};
  const locale = opts.locale;
  return {
    // en-CA renders YYYY-MM-DD regardless of the display locale — key, not label.
    key: new Intl.DateTimeFormat("en-CA", { ...tz, year: "numeric", month: "2-digit", day: "2-digit" }),
    weekday: new Intl.DateTimeFormat(locale, { ...tz, weekday: "short" }),
    dayOfMonth: new Intl.DateTimeFormat(locale, { ...tz, day: "numeric" }),
    label: new Intl.DateTimeFormat(locale, { ...tz, weekday: "long", month: "long", day: "numeric" }),
    /* B403 wave-2 residual — the 24h register, whatever the locale's habit. */
    time: new Intl.DateTimeFormat(locale, { ...tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
  };
}

/** Group sellable slots into ordered calendar days for the display zone. */
export function groupDepartures(slots: readonly SlotSummary[], opts: DisplayOptions = {}): readonly DayGroup[] {
  const fmt = formatters(opts);
  const onSale = [...slots].filter((s) => s.status === "ON_SALE").sort((a, b) => a.start - b.start);
  const groups: DayGroup[] = [];
  for (const slot of onSale) {
    const key = fmt.key.format(slot.start);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) {
      groups[groups.length - 1] = { ...last, departures: [...last.departures, slot] };
    } else {
      groups.push({
        key,
        weekday: fmt.weekday.format(slot.start),
        dayOfMonth: fmt.dayOfMonth.format(slot.start),
        label: fmt.label.format(slot.start),
        departures: [slot],
      });
    }
  }
  return groups;
}

/** The binding constraint across pools: the MINIMUM. No pools = unknown, not zero. */
export function seatsLeft(pools: readonly PoolAvailability[]): number | undefined {
  if (pools.length === 0) return undefined;
  return pools.reduce((min, p) => Math.min(min, p.available), Number.POSITIVE_INFINITY);
}

const LOW_SEATS = 5;

export function DeparturePicker(props: {
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  /** B31h(a): slotId → the server's §2.4 verdict; absent = no verdict. */
  readonly fits?: Readonly<Record<string, boolean>>;
  readonly onPick: (slotId: string) => void;
  readonly locale?: string;
  readonly timeZone?: string;
  /** Controlled selection for tests/deep links; uncontrolled otherwise. */
  readonly selectedDayKey?: string;
  readonly onSelectDay?: (key: string) => void;
  /** B345 — the way back to the catalog, drawn on the refusal. */
  readonly onBrowseAll?: () => void;
  /** B521 — the product.calendar answer; absent = no figures drawn. */
  readonly pricing?: CalendarPricingWire;
}): ReactElement {
  const opts: DisplayOptions = { ...(props.locale !== undefined ? { locale: props.locale } : {}), ...(props.timeZone !== undefined ? { timeZone: props.timeZone } : {}) };
  const groups = groupDepartures(props.slots, opts);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  if (groups.length === 0) {
    /* B345 — this branch is reachable only when NOTHING is ON_SALE, so it
       cannot be "sold out" (a sold-out departure is ON_SALE and draws as a
       disabled tile below), it was never given a date to call "this date",
       and the day-list a "try another date" would point at lives inside the
       branch this return declines to render. The words are derived instead. */
    const face = emptyDepartureFace(props.slots);
    return <EmptyState title={face.title} body={face.body} action={browseAllDoor(props.onBrowseAll)} />;
  }

  const requested = props.selectedDayKey ?? picked;
  const selected = groups.find((g) => g.key === requested) ?? groups[0]!;
  const fmt = formatters(opts);
  const dayPrices = props.pricing === undefined ? {} : dayFromPrices(props.pricing, groups);
  const flatFrom = props.pricing?.kind === "FLAT" ? props.pricing.from : undefined;

  return (
    <div className="departures">
      <ul className="day-list">
        {groups.map((day) => (
          <li key={day.key}>
            <PickerTile
              kicker={day.weekday}
              value={day.dayOfMonth}
              {...(dayPrices[day.key] !== undefined
                ? { note: tilePrice(dayPrices[day.key]!, props.locale), label: `${day.label}, from ${formatMoney(dayPrices[day.key]!, props.locale)}` }
                : { label: day.label })}
              pressed={day.key === selected.key}
              onPress={() => {
                setPicked(day.key);
                props.onSelectDay?.(day.key);
              }}
            />
          </li>
        ))}
      </ul>
      <h3 className="day-label">
        {selected.label}
        {flatFrom !== undefined ? <span className="day-from-price"> · From {tilePrice(flatFrom, props.locale)}</span> : null}
      </h3>
      <ul className="time-list">
        {selected.departures.map((d) => {
          const left = seatsLeft(props.availability[d.slotId] ?? []);
          const soldOut = left !== undefined && left <= 0;
          const low = left !== undefined && left > 0 && left <= LOW_SEATS;
          /* B31h(a): the server's verdict outranks the chip — a departure the
             party cannot fit is disabled with buyer-language copy, never a
             dead-end pick (the ruled TICKETS_FIRST calendar). */
          const noFit = props.fits?.[d.slotId] === false;
          return (
            <li key={d.slotId}>
              <PickerTile
                value={fmt.time.format(d.start)}
                {...(noFit
                  ? { note: "Doesn’t fit your party", noteTone: "low" as const }
                  : left !== undefined
                    ? { note: soldOut ? "Sold out" : `${left} left`, ...(low || soldOut ? { noteTone: "low" as const } : {}) }
                    : {})}
                disabled={soldOut || noFit}
                onPress={() => props.onPick(d.slotId)}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
